import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BatteryCharging, LucideAngularModule, Pencil, Plus, Trash2, X } from 'lucide-angular';
import { BatteryConfiguration, Vendor } from '../../../core/models/domain.models';
import { ToastService } from '../../../shared/ui/toast.service';
import { BatteryConfigurationsService } from '../services/battery-configurations.service';
import { VendorsService } from '../../vendor/services/vendors.service';

type BatteryForm = {
  vendorId: string;
  vendorSearch: string;
  plantType: string;
  powerCapacityMw: string;
  energyCapacityMw: string;
  chargingPowerMw: string;
  dischargingPowerMw: string;
  socInitial: string;
  socFinal: string;
  chargingEfficiency: string;
  dischargingEfficiency: string;
  cyclePerDay: string;
};

@Component({
  standalone: true,
  selector: 'app-battery-configuration-page',
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './battery-configuration.page.html',
  styleUrl: './battery-configuration.page.scss',
})
export class BatteryConfigurationPage {
  private readonly service = inject(BatteryConfigurationsService);
  private readonly vendorsService = inject(VendorsService);
  private readonly toast = inject(ToastService);

  protected readonly BatteryChargingIcon = BatteryCharging;
  protected readonly PlusIcon = Plus;
  protected readonly PencilIcon = Pencil;
  protected readonly Trash2Icon = Trash2;
  protected readonly XIcon = X;

  protected readonly loading = signal(false);
  protected readonly vendorsLoading = signal(false);
  protected readonly submitting = signal(false);
  protected readonly showForm = signal(false);
  protected readonly vendorSearchOpen = signal(false);
  protected readonly editingId = signal<string | null>(null);
  protected readonly formError = signal('');
  protected readonly entries = signal<BatteryConfiguration[]>([]);
  protected readonly vendors = signal<Vendor[]>([]);
  protected readonly form = signal<BatteryForm>(this.emptyForm());
  protected readonly sortedEntries = computed(() =>
    [...this.entries()].sort((left, right) => left.entry_id.localeCompare(right.entry_id, undefined, { numeric: true })),
  );
  protected readonly filteredVendors = computed(() => {
    const query = this.form().vendorSearch.trim().toLowerCase();
    return [...this.vendors()]
      .sort((left, right) => left.vendor_name.localeCompare(right.vendor_name))
      .filter((vendor) => {
        if (!query) return true;
        return [vendor.vendor_name, vendor.noar_id, vendor.vendor_type, vendor.state]
          .filter((value): value is string => !!value)
          .some((value) => value.toLowerCase().includes(query));
      })
      .slice(0, 8);
  });

  constructor() {
    this.loadVendors();
    this.load();
  }

  protected openAdd(): void {
    this.editingId.set(null);
    this.formError.set('');
    this.form.set(this.emptyForm());
    this.showForm.set(true);
  }

  protected openEdit(entry: BatteryConfiguration): void {
    this.editingId.set(entry.id);
    this.formError.set('');
    const vendor = entry.vendor_id ? this.vendors().find((item) => item.id === entry.vendor_id) : null;
    this.form.set({
      vendorId: entry.vendor_id || '',
      vendorSearch: vendor?.vendor_name || '',
      plantType: entry.plant_type,
      powerCapacityMw: String(entry.power_capacity_mw),
      energyCapacityMw: String(entry.energy_capacity_mw),
      chargingPowerMw: String(entry.charging_power_mw),
      dischargingPowerMw: String(entry.discharging_power_mw),
      socInitial: String(entry.soc_initial),
      socFinal: String(entry.soc_final),
      chargingEfficiency: String(entry.charging_efficiency),
      dischargingEfficiency: String(entry.discharging_efficiency),
      cyclePerDay: String(entry.cycle_per_day),
    });
    this.vendorSearchOpen.set(false);
    this.showForm.set(true);
  }

  protected closeForm(): void {
    this.showForm.set(false);
    this.editingId.set(null);
    this.vendorSearchOpen.set(false);
    this.formError.set('');
  }

  protected setField<K extends keyof BatteryForm>(key: K, value: BatteryForm[K]): void {
    this.form.update((current) => ({ ...current, [key]: value }));
  }

  protected openVendorSearch(): void {
    this.vendorSearchOpen.set(true);
  }

  protected updateVendorSearch(value: string): void {
    const normalized = value.toString();
    const selectedVendor = this.form().vendorId ? this.vendors().find((item) => item.id === this.form().vendorId) : null;
    this.form.update((current) => ({
      ...current,
      vendorSearch: normalized,
      vendorId: selectedVendor?.vendor_name === normalized ? current.vendorId : '',
      plantType: selectedVendor?.vendor_name === normalized ? current.plantType : '',
    }));
    this.vendorSearchOpen.set(true);
  }

  protected selectVendor(vendor: Vendor): void {
    this.form.update((current) => ({
      ...current,
      vendorId: vendor.id,
      vendorSearch: vendor.vendor_name,
      plantType: vendor.vendor_name,
    }));
    this.vendorSearchOpen.set(false);
  }

  protected clearVendor(): void {
    this.form.update((current) => ({
      ...current,
      vendorId: '',
      vendorSearch: '',
      plantType: '',
    }));
    this.vendorSearchOpen.set(false);
  }

  protected save(): void {
    if (this.submitting()) return;
    const raw = this.form();
    const plantType = raw.plantType.trim();
    const payload: Partial<BatteryConfiguration> = {
      vendor_id: raw.vendorId || null,
      plant_type: plantType,
      power_capacity_mw: Number(raw.powerCapacityMw),
      energy_capacity_mw: Number(raw.energyCapacityMw),
      charging_power_mw: Number(raw.chargingPowerMw),
      discharging_power_mw: Number(raw.dischargingPowerMw),
      soc_initial: Number(raw.socInitial),
      soc_final: Number(raw.socFinal),
      charging_efficiency: Number(raw.chargingEfficiency),
      discharging_efficiency: Number(raw.dischargingEfficiency),
      cycle_per_day: Number(raw.cyclePerDay),
    };

    if (!raw.vendorId && !this.editingId()) {
      this.formError.set('Vendor is required.');
      return;
    }

    if (!plantType) {
      this.formError.set('Plant Type is required.');
      return;
    }

    if (Object.values(payload).some((value) => typeof value === 'number' && !Number.isFinite(value))) {
      this.formError.set('All numeric fields are required.');
      return;
    }

    this.formError.set('');
    this.submitting.set(true);
    const isEditing = !!this.editingId();
    const request = this.editingId()
      ? this.service.update(this.editingId()!, payload)
      : this.service.create(payload);
    request.subscribe({
      next: () => {
        this.submitting.set(false);
        this.closeForm();
        this.toast.show(isEditing ? 'Battery entry updated' : 'Battery entry created', '', 'success');
        this.load();
      },
      error: () => {
        this.submitting.set(false);
        this.formError.set('Failed to save battery entry.');
      },
    });
  }

  protected remove(entry: BatteryConfiguration): void {
    if (!window.confirm(`Delete ${entry.entry_id}?`)) {
      return;
    }
    this.service.delete(entry.id).subscribe({
      next: () => {
        this.toast.show('Battery entry deleted', '', 'success');
        this.load();
      },
      error: () => this.toast.show('Failed to delete battery entry', '', 'danger'),
    });
  }

  private emptyForm(): BatteryForm {
    return {
      vendorId: '',
      vendorSearch: '',
      plantType: '',
      powerCapacityMw: '',
      energyCapacityMw: '',
      chargingPowerMw: '',
      dischargingPowerMw: '',
      socInitial: '',
      socFinal: '',
      chargingEfficiency: '',
      dischargingEfficiency: '',
      cyclePerDay: '',
    };
  }

  private loadVendors(): void {
    this.vendorsLoading.set(true);
    this.vendorsService.list().subscribe({
      next: (rows) => {
        this.vendors.set(rows);
        this.vendorsLoading.set(false);
      },
      error: () => {
        this.vendors.set([]);
        this.vendorsLoading.set(false);
        this.toast.show('Failed to load vendors', '', 'danger');
      },
    });
  }

  private load(): void {
    this.loading.set(true);
    this.service.list().subscribe({
      next: (rows) => {
        this.entries.set(rows);
        this.loading.set(false);
      },
      error: () => {
        this.entries.set([]);
        this.loading.set(false);
        this.toast.show('Failed to load battery configurations', '', 'danger');
      },
    });
  }
}
